import React from 'react'

import { NotificationInfoFragment } from '@sketch/gql-types'

import { AddProjectMemberPreview } from './AddProjectMemberPreview'
import { AddShareMemberPreview } from './AddShareMemberPreview'
import { CommentMentionPreview } from './CommentMentionPreview'
import { DownloadableAssetPreview } from './DownloadableAssetPreview'
import { FolderNotificationPreview } from './FolderNotificationPreview'
import { TrashIcon } from './FolderNotificationPreview.styles'
import { FolderTileMedia } from './FolderTileMedia'
import { StarPreview } from './StarPreview'
import { WorkspaceInviteAcceptedPreview } from './WorkspaceInviteAcceptedPreview'
import { WorkspaceInvitedPreview } from './WorkspaceInvitedPreview'
import { IconTile } from './TileCard.styles'
import { RichPreviewBox, RichPreviewText } from './InboxPreview.styles'

interface InboxPreviewBodyProps {
  notification: NotificationInfoFragment
}

export const InboxPreviewBody = ({ notification }: InboxPreviewBodyProps) => {
  if (
    notification.__typename === 'CommentNotification' ||
    notification.__typename === 'MentionNotification'
  ) {
    return <CommentMentionPreview notification={notification} />
  }

  if (notification.__typename === 'DownloadableAssetRenderedNotification') {
    return <DownloadableAssetPreview notification={notification} />
  }

  if (notification.__typename === 'AddShareMemberNotification') {
    return <AddShareMemberPreview notification={notification} />
  }

  if (notification.__typename === 'AddProjectMemberNotification') {
    return <AddProjectMemberPreview notification={notification} />
  }

  if (notification.__typename === 'StarNotification') {
    return <StarPreview notification={notification} />
  }

  if (
    notification.__typename === 'FolderArchivedNotification' ||
    notification.__typename === 'FolderTrashedNotification' ||
    notification.__typename === 'FolderRestoredNotification'
  ) {
    // Archived, trashed and restored folders still exist, so we preview their
    // contents (a 2×2 grid of document thumbnails) and label the tile with the
    // folder name; a missing folder leaves nothing to preview.
    if (!notification.folder) return null

    return (
      <FolderNotificationPreview
        media={<FolderTileMedia folder={notification.folder} />}
        folderName={notification.folder.name}
        workspace={notification.workspace}
      />
    )
  }

  if (notification.__typename === 'FolderPermanentlyDeletedNotification') {
    // A permanently-deleted folder no longer exists, so the type doesn't expose
    // the project (and therefore its contents or name). We fall back to a trash
    // glyph tile, and the folder name carried on the title labels it.
    return (
      <FolderNotificationPreview
        media={
          <IconTile aria-hidden>
            <TrashIcon />
          </IconTile>
        }
        workspace={notification.workspace}
        folderName={notification.title}
      />
    )
  }

  if (notification.__typename === 'WorkspaceInvitedNotification') {
    return <WorkspaceInvitedPreview notification={notification} />
  }

  if (notification.__typename === 'WorkspaceInviteAcceptedNotification') {
    return <WorkspaceInviteAcceptedPreview notification={notification} />
  }

  return (
    <RichPreviewBox>
      <RichPreviewText>Rich Summary / Preview</RichPreviewText>
    </RichPreviewBox>
  )
}
